import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "../axiosInstance";
import "../pages/banktransfer.css";
import logo from "../assets/logo.png";
import { useCommonVars } from "./useCommonVars";

const MAX_SIZE_MB = 50;

function BankTransferPage({ user }) {
  const [copiedMessage, setCopiedMessage] = useState("");
  const [details, setDetails] = useState(null);
  const [errorMsgPay, setErrorMsgPay] = useState("");
  const [loading, setLoading] = useState(true);
  const {
    bankCard,
    bankCard2,
    sbpCard,
    sbpCard2,
    bankName,
    bankName2,
    supportUrl,
    maxInvest_s,
    maxInvest_s_2,
    bank_name_card,
    bank_name_card2,
    recipient_card,
    recipient_card2,
    recipient_SBP,
    recipient_SBP2
  } = useCommonVars(user);

  const navigate = useNavigate();
  const location = useLocation();
  const amount = location.state?.amount ?? 0;
  const method = location.state?.method ?? "sbp";
  const [uploadedFile, setUploadedFile] = useState(null);
  const [showUploadError, setShowUploadError] = useState(false);
  const [uploadErrorMsg, setUploadErrorMsg] = useState("");
  const [highlight, setHighlight] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [blockActions, setBlockActions] = useState(false);
  const uploadRef = useRef();
  const fileInputRef = useRef();
  const actionsRef = useRef();
  const fallbackInputRef = useRef();
  const addedIosRef = useRef(false);
  const addedPushRef = useRef(false);

  useEffect(() => {
    const ua = navigator.userAgent || "";
    const isIphone = /iPhone|iPod/.test(ua);
    if (isIphone) {
      document.documentElement.classList.add("ios");
      addedIosRef.current = true;
    } else {
      document.documentElement.classList.add("push-2cm");
      addedPushRef.current = true;
    }
    document.documentElement.classList.add("av-hidden");
    return () => {
      document.documentElement.classList.remove("av-hidden");
      if (addedIosRef.current) document.documentElement.classList.remove("ios");
      if (addedPushRef.current) document.documentElement.classList.remove("push-2cm");
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setErrorMsgPay("");
    setDetails(null);

    const buildDetails = (useSecond) => {
      let bank, receiver, card_number;
      if (method === "sbp") {
        bank = useSecond ? (bankName2 || bankName || "-") : (bankName || "-");
        receiver = useSecond ? (recipient_SBP2 || "-") : (recipient_SBP || "-");
        card_number = useSecond ? (sbpCard2 || sbpCard || "-") : (sbpCard || "-");
      } else if (method === "card") {
        bank = useSecond ? (bank_name_card2 || bankName2 || bankName || "-") : (bank_name_card || bankName || "-");
        receiver = useSecond ? (recipient_card2 || "-") : (recipient_card || "-");
        card_number = useSecond ? (bankCard2 || bankCard || "-") : (bankCard || "-");
      } else {
        bank = bankName || "-";
        receiver = "-";
        card_number = "-";
      }
      return {
        amount,
        bank,
        receiver,
        card_number,
        pay_id: `manual${useSecond ? "2" : ""}_` + Date.now()
      };
    };

    if (maxInvest_s_2 != null && amount <= maxInvest_s_2) {
      const d = buildDetails(true);
      if (isMounted) {
        setDetails(d);
        setLoading(false);
      }
    } else if (amount <= maxInvest_s) {
      const d = buildDetails(false);
      if (isMounted) {
        setDetails(d);
        setLoading(false);
      }
    } else {
      axios
        .post("/payments/create/", { amount, method }, { withCredentials: true })
        .then((res) => {
          if (!isMounted) return;
          const data = res.data;
          if (data.detail) {
            setErrorMsgPay(data.detail || "Не удалось получить реквизиты. Попробуйте позже.");
            return;
          }
          setDetails(data);
        })
        .catch(() => {
          setErrorMsgPay("Не удалось получить реквизиты. Попробуйте позже.");
        })
        .finally(() => {
          if (isMounted) setLoading(false);
        });
    }

    return () => {
      isMounted = false;
    };
  }, [
    amount,
    method,
    user,
    bankCard,
    bankCard2,
    sbpCard,
    sbpCard2,
    bankName,
    bankName2,
    maxInvest_s,
    maxInvest_s_2,
    bank_name_card,
    bank_name_card2,
    recipient_card,
    recipient_card2,
    recipient_SBP,
    recipient_SBP2
  ]);

  const copy = async (text, label) => {
    let copied = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(String(text || ""));
        copied = true;
      }
    } catch {}
    if (!copied) {
      try {
        const textarea = document.createElement("textarea");
        textarea.value = String(text || "");
        textarea.setAttribute("readonly", "");
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        textarea.style.pointerEvents = "none";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        textarea.blur();
        document.body.removeChild(textarea);
        copied = true;
      } catch {}
    }
    if (!copied) {
      if (fallbackInputRef.current) {
        fallbackInputRef.current.value = String(text || "");
        fallbackInputRef.current.style.display = "block";
        fallbackInputRef.current.focus();
        fallbackInputRef.current.select();
        setCopiedMessage("Текст выделен, скопируйте вручную");
        setTimeout(() => {
          fallbackInputRef.current.style.display = "none";
          fallbackInputRef.current.blur();
          document.activeElement.blur();
          setCopiedMessage("");
        }, 3200);
        return;
      }
      setCopiedMessage("Скопируйте вручную");
      setTimeout(() => setCopiedMessage(""), 2200);
      return;
    }
    setCopiedMessage(`${label} скопировано`);
    setTimeout(() => {
      setCopiedMessage("");
      document.activeElement.blur();
    }, 1400);
  };

  const goBack = () => {
    if (blockActions || submitting) return;
    setBlockActions(true);
    navigate("/card");
    setTimeout(() => setBlockActions(false), 800);
  };

  const handleFileSelect = (file) => {
    if (!file) return;
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      setUploadedFile(null);
      setUploadErrorMsg(`Файл слишком большой (максимум ${MAX_SIZE_MB} МБ)`);
      setShowUploadError(true);
      setHighlight(true);
      setTimeout(() => setHighlight(false), 1600);
      setTimeout(() => setShowUploadError(false), 3500);
      setTimeout(() => {
        if (uploadRef.current) {
          uploadRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
      return;
    }
    setUploadedFile(file);
    setUploadErrorMsg("");
    setShowUploadError(false);
    setHighlight(false);
    setTimeout(() => {
      if (actionsRef.current) {
        actionsRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 150);
  };

  const handleConfirm = async () => {
    if (blockActions || submitting) return;
    if (!uploadedFile) {
      setHighlight(true);
      setShowUploadError(true);
      setUploadErrorMsg("Пожалуйста, прикрепите скрин/файл подтверждения перевода");
      setTimeout(() => setHighlight(false), 1600);
      setTimeout(() => setShowUploadError(false), 3500);
      setTimeout(() => {
        if (uploadRef.current) {
          uploadRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
      return;
    }
    setBlockActions(true);
    setHighlight(false);
    setShowUploadError(false);
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("user_id", user?.telegram_id);
      formData.append("pay_id", details.pay_id);
      formData.append("method", method);
      formData.append("amount", details.amount);
      formData.append("file", uploadedFile);
      const res = await axios.post("/project/send-payment-message", formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      if (res.data?.status === "ok") {
        navigate("/card", {
          state: {
            confirmMessage: "✅ Заявка отправлена. Ожидайте подтверждения администратора."
          }
        });
      } else {
        alert("❌ Не удалось отправить заявку. Повторите позже.");
      }
    } catch {
      alert("⚠️ Ошибка при подтверждении. Попробуйте позже.");
    } finally {
      setSubmitting(false);
      setBlockActions(false);
    }
  };

  const uploadSection = (
    <div ref={uploadRef} className={`upload-section${highlight ? " upload-section-highlight" : ""}`}>
      {showUploadError && (
        <div className="upload-error-block">
          <div className="upload-error-text">
            <svg style={{width:22,height:22,marginRight:6}} viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="10" fill="#fecaca"/>
              <path d="M10 6v4" stroke="#dc2626" strokeWidth="2" strokeLinecap="round"/>
              <circle cx="10" cy="14" r="1.3" fill="#dc2626"/>
            </svg>
            {uploadErrorMsg || "Ошибка загрузки файла"}
          </div>
          <div className="upload-error-arrow">
            <svg width="28" height="28" viewBox="0 0 28 28">
              <path d="M14 6v13m0 0-6.5-6.5M14 19l6.5-6.5" stroke="#dc2626" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>
      )}
      <label className="upload-label" style={{ width: "100%", display: "block" }}>
        Загрузите скрин/файл перевода:
        <input
          type="file"
          ref={fileInputRef}
          accept="image/*,application/pdf"
          style={{ display: "none" }}
          onChange={(e) => handleFileSelect(e.target.files[0])}
        />
        <div
          className="upload-dropzone"
          tabIndex={0}
          onDrop={(e) => {
            e.preventDefault();
            handleFileSelect(e.dataTransfer.files[0]);
          }}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => fileInputRef.current && fileInputRef.current.click()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              fileInputRef.current && fileInputRef.current.click();
            }
          }}
        >
          <div style={{ width: "100%" }}>
            <button
              type="button"
              className="btn-confirm2"
              style={{ width: 195, fontSize: 15, margin: "0 auto 6px auto", display: "block", background: "#8B5CF6" }}
              tabIndex={-1}
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current && fileInputRef.current.click();
              }}
            >
              {uploadedFile ? "Заменить файл" : "Выбрать файл"}
            </button>
            <div style={{ color: "#A5B4FC", fontSize: "14px", opacity: 0.95, marginTop: 3 }}>
              или перетащите сюда файл
            </div>
          </div>
        </div>
      </label>
      {uploadedFile && (
        <div className="upload-file-info" style={{ marginTop: 10 }}>
          {uploadedFile.type.startsWith("image/")
            ? <img src={URL.createObjectURL(uploadedFile)} alt="preview" className="upload-preview" style={{ maxWidth: 160, maxHeight: 120, marginBottom: 8 }}/>
            : <div className="upload-filename">{uploadedFile.name}</div>
          }
          <div>
            <span style={{ fontSize: 13, color: "#ccc" }}>({(uploadedFile.size / 1024 / 1024).toFixed(2)} МБ)</span>
            <button className="upload-remove-btn" style={{ marginLeft: 10 }} onClick={() => setUploadedFile(null)}>Удалить</button>
          </div>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="loader-center">
        <div className="loader-wrapper">
          <div className="loader-logo-wrapper1">
            <img src={logo} alt="" className="loader-logo"/>
          </div>
          <div className="glow-circle"></div>
          <div className="loading-text">
            Получаем реквизиты для оплаты
            <span className="dot-animate">
              <span>.</span>
              <span>.</span>
              <span>.</span>
            </span>
          </div>
          <div className="loader-hint">Обычно занимает не более 30 секунд</div>
        </div>
      </div>
    );
  }

  if (errorMsgPay) {
    return (
      <div className="bank-transfer-container">
        <div className="error-msg-pay">
          {errorMsgPay}
          <div className="error-msg-hint">Попробуйте изменить сумму или способ оплаты.</div>
          <div className="error-msg-support">
            Или <a href={supportUrl} style={{ color: "#7C3AED", textDecoration: "underline" }}>обратитесь в поддержку</a>
          </div>
        </div>
        <div className="transfer-actions" style={{ marginTop: 30 }}>
          <button className="btn-cancel2" onClick={goBack} disabled={blockActions || submitting}>Назад</button>
        </div>
      </div>
    );
  }

  if (!details) return null;

  return (
    <div className="bank-transfer-container">
      {copiedMessage && <div className="copy-toast">{copiedMessage}</div>}
      <input
        ref={fallbackInputRef}
        readOnly
        style={{
          position: "fixed",
          top: "-60px",
          left: 0,
          opacity: 0,
          zIndex: 9999,
          fontSize: 16,
          pointerEvents: "none",
          display: "none"
        }}
      />
      <div className="transfer-tip">
        Скопируйте реквизиты, откройте приложение<br/>
        банка и отправьте перевод одним платежом.
        <div className="transfer-deadline">Время на оплату: 20 минут.</div>
      </div>

      <div className="transfer-field">
        <label>Банк:</label>
        <div className="transfer-row">
          <span>{details.bank || "-"}</span>
          <button className="copy-btn" onClick={() => copy(details.bank, "Банк")}>Скопировать</button>
        </div>
      </div>

      <div className="transfer-field">
        <label>Получатель:</label>
        <div className="transfer-row">
          <span>{details.receiver || "-"}</span>
          <button className="copy-btn" onClick={() => copy(details.receiver, "Получатель")}>Скопировать</button>
        </div>
      </div>

      <div className="transfer-field">
        <label>Номер:</label>
        <div className="transfer-row">
          <span style={{ wordBreak: "break-word" }}>{details.card_number || "-"}</span>
          <button className="copy-btn" onClick={() => copy(details.card_number, "Номер")}>Скопировать</button>
        </div>
      </div>

      <div className="transfer-field">
        <label>Сумма перевода:</label>
        <div className="transfer-row">
          <span>{details.amount || "-"}</span>
          <button className="copy-btn" onClick={() => copy(details.amount, "Сумма")}>Скопировать</button>
        </div>
      </div>

      {uploadSection}

      <div className="transfer-actions" ref={actionsRef}>
        <button className="btn-cancel2" onClick={goBack} disabled={submitting || loading || blockActions}>Отмена</button>
        <button className="btn-confirm2" onClick={handleConfirm} disabled={submitting || loading || blockActions}>
          {submitting ? "Отправка..." : "Подтвердить"}
        </button>
      </div>
    </div>
  );
}

export default BankTransferPage;


